import { useContext, useState, useEffect, useMemo, useRef } from "react";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
// import ShareTwoToneIcon from "@mui/icons-material/ShareTwoTone";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import TungstenIcon from "@mui/icons-material/Tungsten";
import TungstenOutlinedIcon from "@mui/icons-material/TungstenOutlined";
import SensorDoorIcon from "@mui/icons-material/SensorDoor";
import SensorDoorOutlinedIcon from "@mui/icons-material/SensorDoorOutlined";
import MinorCrashIcon from "@mui/icons-material/MinorCrash";
import MinorCrashOutlinedIcon from "@mui/icons-material/MinorCrashOutlined";

import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import {
  ANIMATIONS,
  CAR_DOOR_CLOSE_SOUND,
  CAR_DOOR_OPEN_SOUND,
  LAMPS_OFF_VARIANT_EXT,
  LAMPS_ON_VARIANT_EXT,
  MODELS,
  STREAM_TYPES,
  SUN_STATE,
  VIEW_TYPES,
} from "shared/enum";
import useVariant from "hooks/useVariant";
import useGtagVariantData from "hooks/useGtagVariantData";
import IconButton from "./IconButton";
import getEnvLoadLevelsObject from "helpers/getEnvLoadLevelsObject";
import { getWasmMethods } from "@3ds/3DEXCITEshow-streaming";

import type { CachedState, FeaturesAndColorsFile } from "types/app";
import { useMediaQuery, useTheme } from "@mui/material";

/**
 * Min. time required to wait before allowing animation change on the same type
 */
const TIMEOUT = 2000;

const doorOpenSound = new Audio(CAR_DOOR_OPEN_SOUND);
const doorCloseSound = new Audio(CAR_DOOR_CLOSE_SOUND);

type TimeoutRef = {
  [key: string]: number;
};

type CollapsibleGroupProps = {
  open: boolean;
  children?: JSX.Element;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CollapsibleGroup({ open, children }: CollapsibleGroupProps) {
  const animation = `${open ? slideInAnimation : slideOutAnimation} 0.2s ease-in-out forwards`;

  return <Stack sx={{ animation }}>{children}</Stack>;
}

type RightPanelProps = {
  topBarRef: React.RefObject<HTMLDivElement>;
  rightPanelRef: React.Ref<HTMLDivElement>;
  isMobile: boolean;
  handleSaveClick: () => void;
  handleShareClick: () => void;
  handleLoadScreen: (val: boolean) => void;
  drawerOpen?: boolean;
};

export default function RightPanel({
  topBarRef,
  rightPanelRef,
  isMobile,
  handleSaveClick, // eslint-disable-line @typescript-eslint/no-unused-vars
  handleShareClick, // eslint-disable-line @typescript-eslint/no-unused-vars
  handleLoadScreen,
  drawerOpen,
}: RightPanelProps) {
  const { animationBlacklist, cachedState, setCachedState } = useContext(DataContext);
  const {
    cameras,
    demoState,
    scenarios,
    turntableState,
    setTurntableState,
    setDemoState,
    streamViewType,
  } = useContext(StreamContext);

  const { isTurntable } = turntableState;

  const variant = useVariant();
  const getGtagData = useGtagVariantData();
  const [expanded, setExpanded] = useState(false);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const timeoutRef = useRef<TimeoutRef>({});
  const theme = useTheme();

  const { animations } = cachedState;
  const { dayMode, doorsOpen, headlights, sunroofOpen } = animations;
  const wasmMethods = getWasmMethods();

  const { selectedCameraView } = demoState;
  const disablePanel = streamViewType === STREAM_TYPES.IN_GLO;

  const [isDaymodeLoading, setIsDaymodeLoading] = useState(false);
  const [isDoorsOpenLoading, setIsDoorsOpenLoading] = useState(false);
  const [isHeadlightsLoading, setIsHeadlightsLoading] = useState(false);
  const [isSunroofOpenLoading, setIsSunroofOpenLoading] = useState(false);

  const interiorMarsCamera = cameras?.find((cam) => cam?.name === "CAM_ORBIT_MARS");

  const isTabLandscape = useMediaQuery(
    theme.breakpoints.between("sm", "md") + " and (orientation: landscape)",
  );
  const isDrawerOpenOnSm = isTabLandscape && drawerOpen;

  const unavailableAnimations = useMemo(() => {
    if (!variant) return [];

    return Object.keys(animationBlacklist).filter((key) =>
      animationBlacklist[key as keyof FeaturesAndColorsFile["animationBlacklist"]]?.includes(
        variant.code,
      ),
    );
  }, [animationBlacklist, variant]);

  const setAnimations = (newAnimations: CachedState["animations"]) => {
    setCachedState({
      ...cachedState,
      animations: newAnimations,
    });
  };

  const playBackCommand = async (name: string, timeOut: number) => {
    try {
      await wasmMethods?.playAnimationBackward(name);
      setTimeout(async () => {
        // door close sound
        if (name === ANIMATIONS[variant.code].openDoorsStream.name) {
          doorCloseSound.play();
        }
        // if (name === "ANIM_BLINKERS") {
        //   wasmMethods.stopAnimation("ANIM_BLINKERS");
        // }
        // await wasmMethods?.pauseAnimation(name);
      }, timeOut);
    } catch {
      console.warn(`Error playing animation backward ` + name);
    }
  };

  const handleDoorsOpenVariantsExterior = async (newStatus: boolean) => {
    if (isTurntable) return;
    const isXEV9E = variant.code === MODELS["XEV 9E"].code;

    if (newStatus) {
      await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].openDoorsStream.name);
      await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].chargingPortDoorStream.name);

      // door open sound
      setTimeout(() => {
        doorOpenSound.play();
      }, 500);
    } else {
      playBackCommand(ANIMATIONS[variant.code].openDoorsStream.name, isXEV9E ? 3450 : 3350);
      playBackCommand(ANIMATIONS[variant.code].chargingPortDoorStream.name, 3000);
    }
  };

  const handleDoorsOpenVariantsInterior = async (newStatus: boolean) => {
    if (isTurntable) return;

    if (newStatus) {
      await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].gloveBoxStream.name);
      await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].centerArmRestStream.name);
      await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].seatArmRestStream.name);

      if (variant.code === MODELS["XEV 9E"].code) {
        setTimeout(async () => {
          await wasmMethods?.playAnimationForward(
            ANIMATIONS[variant.code].secondRowSeatStream.name,
          );
        }, 3000);
      }

      // door open sound
      setTimeout(() => {
        doorOpenSound.play();
      }, 500);
    } else {
      playBackCommand(ANIMATIONS[variant.code].gloveBoxStream.name, 3000);
      playBackCommand(ANIMATIONS[variant.code].centerArmRestStream.name, 3000);
      playBackCommand(ANIMATIONS[variant.code].seatArmRestStream.name, 3000);

      if (variant.code === MODELS["XEV 9E"].code) {
        setTimeout(async () => {
          playBackCommand(ANIMATIONS[variant.code].secondRowSeatStream.name, 31000);
        }, 3000);
      }
    }
  };

  const handleHeadlightsVariantsExterior = async (newStatus: boolean, lampVariants: string[]) => {
    if (isTurntable) return;

    await wasmMethods?.switchVariants(...lampVariants);

    // WELCOME ANIMATION
    if (variant.code === MODELS["BE 6"].code) {
      if (!newStatus) {
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].welcomeHello.name);
        // await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].symphonyRev.name);
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].marsRoof.name);
      } else {
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].goodByeHello.name);

        playBackCommand(ANIMATIONS[variant.code].symphonyRevStream.name, 1500);
      }
    } else {
      if (!newStatus) {
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].welcomeHello.name);
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].marsRoof.name);
      } else {
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].goodByeHello.name);
      }
    }
  };

  const handleHeadlightsVariantsInterior = async (newStatus: boolean) => {
    if (isTurntable) return;

    const lampVariantsInterior = newStatus ? LAMPS_ON_VARIANT_EXT : LAMPS_OFF_VARIANT_EXT;
    await wasmMethods?.switchVariants(...lampVariantsInterior);

    // MARS ROOF ANIMATION
    if (variant.code === MODELS["BE 6"].code) {
      if (newStatus) {
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].marsRoof.name);
      } else {
        playBackCommand(ANIMATIONS[variant.code].welcomeHello.name, 31000);
      }
    } else {
      if (newStatus) {
        await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].marsRoof.name);
      } else {
        playBackCommand(ANIMATIONS[variant.code].marsRoof.name, 3000);
      }
    }
  };

  const handleAnimationChange = async (
    animationType: keyof CachedState["animations"],
    status?: boolean,
  ) => {
    const now = Date.now();

    if (animationType in timeoutRef.current && now - timeoutRef.current[animationType] < TIMEOUT) {
      return;
    }

    const newStatus = typeof status === "undefined" ? !animations[animationType] : status;

    timeoutRef.current[animationType] = now;
    const isXEV9E = variant.code === MODELS["XEV 9E"].code;

    const lampVariants = !newStatus
      ? isXEV9E
        ? ["DRL_ON"]
        : LAMPS_ON_VARIANT_EXT
      : isXEV9E
        ? ["DRL_OFF"]
        : LAMPS_OFF_VARIANT_EXT;
    const onOffLampVariant = newStatus
      ? isXEV9E
        ? ["DRL_ON"]
        : LAMPS_ON_VARIANT_EXT
      : isXEV9E
        ? ["DRL_OFF"]
        : LAMPS_OFF_VARIANT_EXT;

    const goodbyAnimation = `${ANIMATIONS[variant.code].goodByeHello.name}|seek|100`;

    switch (animationType) {
      case "dayMode":
        setIsDaymodeLoading(true);

        if (!isTurntable) {
          handleLoadScreen(true);
          setTimeout(() => {
            handleLoadScreen(false);
          }, 2500);
          await wasmMethods.loadEnvironmentLevels(
            JSON.stringify(
              getEnvLoadLevelsObject({
                environmentId: scenarios[0]?.id,
                cameraId: selectedCameraView?.cameraData?.id,
                dayTime: newStatus
                  ? SUN_STATE[variant.code].dayMode.dayTime
                  : SUN_STATE[variant.code].nightMode.dayTime,
                northyaw: newStatus
                  ? SUN_STATE[variant.code].dayMode.northyaw
                  : SUN_STATE[variant.code].nightMode.northyaw,
                noFade: false,
                applyDaytime: true,
                cameraData: selectedCameraView?.cameraData,
              }),
            ),
          );
        }

        setTimeout(() => {
          if (cachedState.view === VIEW_TYPES.EXTERIOR) {
            //TURN LAMPS ON OFF with animations
            handleHeadlightsVariantsExterior(newStatus, lampVariants);
          }
        }, 4000);
        handleHeadlightsVariantsInterior(cachedState.animations.dayMode);

        setTurntableState((prev) => {
          // Filter out existing variants before adding new ones
          const updatedVariants = prev.selectedVariants.filter(
            (variant) =>
              !LAMPS_ON_VARIANT_EXT.includes(variant) && !LAMPS_OFF_VARIANT_EXT.includes(variant),
          );

          // Filter out "Off" strings from lampVariants before appending
          // const filteredLampVariants = lampVariants.filter(
          //   (variant) => !LAMPS_OFF_VARIANT_EXT.includes(variant),
          // );

          return {
            ...prev,
            selectedVariants: [...updatedVariants, ...lampVariants],
          };
        });

        setDemoState((prev) => {
          const isXEV9E = variant.code === MODELS["XEV 9E"].code;

          const isLightOn = lampVariants.some((variant) => LAMPS_ON_VARIANT_EXT.includes(variant));

          // Filter animation commands
          const filteredAnimationCommands = prev.animationCommands.filter(
            (cmd) =>
              !cmd.startsWith(`${ANIMATIONS[variant.code].welcomeHello.name}|seek|`) &&
              !cmd.startsWith(`${ANIMATIONS[variant.code].goodByeHello.name}|seek|`) &&
              // !isXEV9E &&
              // !cmd.startsWith(`${ANIMATIONS[variant.code].symphonyRev.name}|seek|`) &&
              !cmd.startsWith(`${ANIMATIONS[variant.code].marsRoof.name}|seek|`) &&
              (!animations.doorsOpen ||
                !cmd.startsWith(`${ANIMATIONS[variant.code].openDoors.name}|seek|`)),
          );

          // Prepare new animation commands
          let newAnimCommand = [];

          if (!newStatus) {
            newAnimCommand = [
              `${ANIMATIONS[variant.code].welcomeHello.name}|seek|100`,
              `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
            ];

            // if (!isXEV9E) {
            //   newAnimCommand.push(`${ANIMATIONS[variant.code].symphonyRev.name}|seek|100`);
            // }
          }

          if (animations.doorsOpen) {
            if (cachedState.view === VIEW_TYPES.EXTERIOR) {
              newAnimCommand.push(`${ANIMATIONS[variant.code].openDoors.name}|seek|100`);
            } else if (cachedState.view === VIEW_TYPES.INTERIOR) {
              newAnimCommand.push(`${ANIMATIONS[variant.code].openDoors.name}|seek|100`);
            }
          }

          // Return updated state
          return {
            ...prev,
            sunState: newStatus
              ? SUN_STATE[MODELS[variant.code].code].dayMode
              : SUN_STATE[MODELS[variant.code].code].nightMode,
            animationCommands: [
              ...filteredAnimationCommands,
              ...newAnimCommand,
              ...(!isLightOn ? [goodbyAnimation] : []),
            ],
          };
        });

        setTimeout(
          () => {
            setIsDaymodeLoading(false);
          },
          isTurntable ? 500 : newStatus ? 20000 : 42000,
        );
        break;

      case "doorsOpen":
        setIsDoorsOpenLoading(true);
        // Turntable logic
        // if (isTurntable) {
        if (cachedState.view === VIEW_TYPES.EXTERIOR) {
          setDemoState((prev) => {
            const filteredCommands = prev.animationCommands.filter(
              (cmd) => !cmd?.startsWith(`${ANIMATIONS[variant.code].openDoors.name}|seek|`),
            );

            const newCommand = !animations.doorsOpen
              ? [`${ANIMATIONS[variant.code].openDoors.name}|seek|100`]
              : [];

            return {
              ...prev,
              animationCommands: [...filteredCommands, ...newCommand],
            };
          });
        } else if (cachedState.view === VIEW_TYPES.INTERIOR) {
          setDemoState((prev) => {
            const isXEV9E = variant.code === MODELS["XEV 9E"].code;

            const filteredCommands = prev.animationCommands.filter(
              (cmd) => !cmd?.startsWith(`${ANIMATIONS[variant.code].openDoors.name}|seek|`),
            );

            const newCommand = !animations.doorsOpen
              ? [`${ANIMATIONS[variant.code].openDoors.name}|seek|100`]
              : [];

            return {
              ...prev,
              animationCommands: [...filteredCommands, ...newCommand],
            };
          });
        }
        // }

        if (cachedState.view === VIEW_TYPES.EXTERIOR) {
          handleDoorsOpenVariantsExterior(!animations.doorsOpen);
        } else if (cachedState.view === VIEW_TYPES.INTERIOR) {
          await handleDoorsOpenVariantsExterior(!animations.doorsOpen);
          await handleDoorsOpenVariantsInterior(!animations.doorsOpen);
        }

        setTimeout(
          () => {
            setIsDoorsOpenLoading(false);
          },
          isTurntable ? 500 : 3000,
        );

        break;

      case "headlights":
        setIsHeadlightsLoading(true);
        setTurntableState((prev) => {
          // Filter out existing variants before adding new ones
          const updatedVariants = prev.selectedVariants.filter(
            (variant) =>
              !LAMPS_ON_VARIANT_EXT.includes(variant) && !LAMPS_OFF_VARIANT_EXT.includes(variant),
          );

          // Filter out "Off" strings from lampVariants before appending
          // const filteredLampVariants = onOffLampVariant.filter(
          //   (variant) => !LAMPS_OFF_VARIANT_EXT.includes(variant),
          // );

          return {
            ...prev,
            selectedVariants: [...updatedVariants, ...onOffLampVariant],
          };
        });

        if (cachedState.view === VIEW_TYPES.EXTERIOR) {
          // turntable variant change
          const isXEV9E = variant.code === MODELS["XEV 9E"].code;
          setDemoState((prev) => {
            const filteredAnimationCommands = prev.animationCommands.filter(
              (cmd) =>
                !cmd.startsWith(`${ANIMATIONS[variant.code].welcomeHello.name}|seek|`) &&
                !cmd.startsWith(`${ANIMATIONS[variant.code].goodByeHello.name}|seek|`) &&
                !cmd.startsWith(`${ANIMATIONS[variant.code].marsRoof.name}|seek|`),
            );

            const newAnimCommand = !isXEV9E
              ? !animations.headlights
                ? [
                    `${ANIMATIONS[variant.code].welcomeHello.name}|seek|100`,
                    `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
                  ]
                : []
              : [
                  `${ANIMATIONS[variant.code].welcomeHello.name}|seek|100`,
                  `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
                ];

            return {
              ...prev,
              sunState: cachedState.animations.dayMode
                ? SUN_STATE[MODELS[variant.code].code].dayMode
                : SUN_STATE[MODELS[variant.code].code].nightMode,
              animationCommands: !animations.headlights
                ? [...filteredAnimationCommands, ...newAnimCommand]
                : [...filteredAnimationCommands, goodbyAnimation],
            };
          });
          // LIGHTS ON OFF
          handleHeadlightsVariantsExterior(animations.headlights, onOffLampVariant);
        } else if (cachedState.view === VIEW_TYPES.INTERIOR) {
          const isXEV9E = variant.code === MODELS["XEV 9E"].code;

          const lampVariantsInterior = !animations.headlights
            ? isXEV9E
              ? ["DRL_ON"]
              : LAMPS_ON_VARIANT_EXT
            : isXEV9E
              ? ["DRL_OFF"]
              : LAMPS_OFF_VARIANT_EXT;

          setTurntableState((prev) => {
            // Filter out existing variants before adding new ones
            const updatedVariants = prev.selectedVariants.filter(
              (variant) =>
                !LAMPS_ON_VARIANT_EXT.includes(variant) && !LAMPS_OFF_VARIANT_EXT.includes(variant),
            );

            // Filter out "Off" strings from lampVariants before appending
            // const filteredLampVariants = lampVariantsInterior.filter(
            //   (variant) => !LAMPS_OFF_VARIANT_EXT.includes(variant),
            // );

            return {
              ...prev,
              selectedVariants: [...updatedVariants, ...lampVariantsInterior],
            };
          });

          // if (!isXEV9E) {
          setDemoState((prev) => {
            //   const filteredAnimationCommands = prev.animationCommands.filter(
            //     (cmd) => !cmd.startsWith(`${ANIMATIONS[variant.code].marsRoof.name}|seek|`),
            //   );

            const filteredAnimationCommands = prev.animationCommands.filter(
              (cmd) =>
                !cmd.startsWith(`${ANIMATIONS[variant.code].welcomeHello.name}|seek|`) &&
                !cmd.startsWith(`${ANIMATIONS[variant.code].goodByeHello.name}|seek|`) &&
                // !isXEV9E &&
                // !cmd.startsWith(`${ANIMATIONS[variant.code].symphonyRev.name}|seek|`) &&
                !cmd.startsWith(`${ANIMATIONS[variant.code].marsRoof.name}|seek|`),
            );

            // const newAnimCommand = !isXEV9E
            //   ? !animations.headlights
            //     ? [
            //         `${ANIMATIONS[variant.code].welcomeHello.name}|seek|100`,
            //         // !isXEV9E && `${ANIMATIONS[variant.code].symphonyRev.name}|seek|100`,
            //         `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
            //       ]
            //     : []
            //   : [
            //       `${ANIMATIONS[variant.code].welcomeHello.name}|seek|100`,
            //       `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
            //     ];

            const newAnimCommand = !animations.headlights
              ? [
                  `${ANIMATIONS[variant.code].welcomeHello.name}|seek|100`,
                  `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
                ]
              : [
                  ...(animations.sunroofOpen
                    ? [`${ANIMATIONS[variant.code].marsRoof.name}|seek|100`]
                    : []),
                  goodbyAnimation,
                ];

            return {
              ...prev,
              sunState: cachedState.animations.dayMode
                ? SUN_STATE[MODELS[variant.code].code].dayMode
                : SUN_STATE[MODELS[variant.code].code].nightMode,
              animationCommands: [...filteredAnimationCommands, ...newAnimCommand],
            };
          });
          // }
          handleHeadlightsVariantsInterior(!animations.headlights);
        }

        setTimeout(
          () => {
            setIsHeadlightsLoading(false);
          },
          isTurntable ? 500 : animations.headlights ? 7000 : 35000,
        );
        break;

      case "sunroofOpen":
        setIsSunroofOpenLoading(true);
        // if (isTurntable) {
        setDemoState((prev) => {
          const filteredAnimationCommands = prev.animationCommands.filter(
            (cmd) =>
              !cmd.startsWith(`${ANIMATIONS[variant.code].sunroofBlind.name}|seek|`) &&
              !cmd.startsWith(`${ANIMATIONS[variant.code].marsRoof.name}|seek|`),
          );

          const newAnimCommand = !animations.sunroofOpen
            ? [
                `${ANIMATIONS[variant.code].sunroofBlind.name}|seek|100`,
                `${ANIMATIONS[variant.code].marsRoof.name}|seek|100`,
              ]
            : [];

          return {
            ...prev,
            sunState: cachedState.animations.dayMode
              ? SUN_STATE[MODELS[variant.code].code].dayMode
              : SUN_STATE[MODELS[variant.code].code].nightMode,
            animationCommands: [...filteredAnimationCommands, ...newAnimCommand],
          };
        });
        // }

        if (!isTurntable) {
          const isXEV9E = variant.code === MODELS["XEV 9E"].code;
          if (wasmMethods && "loadEnvironmentLevels" in wasmMethods) {
            await wasmMethods.loadEnvironmentLevels(
              JSON.stringify(
                getEnvLoadLevelsObject({
                  environmentId: scenarios[0]?.id,
                  cameraId: interiorMarsCamera?.id,
                  dayTime: cachedState.animations.dayMode
                    ? SUN_STATE[variant.code].dayMode.dayTime
                    : SUN_STATE[variant.code].nightMode.dayTime,
                  northyaw: cachedState.animations.dayMode
                    ? SUN_STATE[variant.code].dayMode.northyaw
                    : SUN_STATE[variant.code].nightMode.northyaw,
                  noFade: false,
                  applyDaytime: true,
                  cameraData: interiorMarsCamera,
                }),
              ),
            );
          }

          if (isXEV9E) {
            if (!animations.sunroofOpen) {
              await wasmMethods?.playAnimationForward(
                ANIMATIONS[variant.code].sunroofBlindStream.name,
              );
              await wasmMethods?.playAnimationForward(ANIMATIONS[variant.code].marsRoof.name);
            } else {
              playBackCommand(ANIMATIONS[variant.code].sunroofBlindStream.name, 3000);
              playBackCommand(ANIMATIONS[variant.code].marsRoof.name, 3000);
            }
          } else {
            if (!animations.sunroofOpen) {
              await wasmMethods?.playAnimationForward(
                ANIMATIONS[variant.code].sunroofBlindStream.name,
              );
            } else {
              playBackCommand(
                ANIMATIONS[variant.code].sunroofBlindStream.name,
                ANIMATIONS[variant.code].sunroofBlindStream.duration,
              );
            }
          }
        }

        setTimeout(
          async () => {
            setIsSunroofOpenLoading(false);

            if (
              wasmMethods &&
              "loadEnvironmentLevels" in wasmMethods &&
              animations.sunroofOpen &&
              !isTurntable
            ) {
              await wasmMethods.loadEnvironmentLevels(
                JSON.stringify(
                  getEnvLoadLevelsObject({
                    environmentId: scenarios[0]?.id,
                    cameraId: selectedCameraView?.camId,
                    dayTime: cachedState.animations.dayMode
                      ? SUN_STATE[variant.code].dayMode.dayTime
                      : SUN_STATE[variant.code].nightMode.dayTime,
                    northyaw: cachedState.animations.dayMode
                      ? SUN_STATE[variant.code].dayMode.northyaw
                      : SUN_STATE[variant.code].nightMode.northyaw,
                    noFade: false,
                    applyDaytime: true,
                    cameraData: selectedCameraView?.cameraData,
                  }),
                ),
              );
            }
          },
          isTurntable ? 500 : 10000,
        );

        break;

      default:
        break;
    }

    setAnimations({
      ...animations,
      [animationType]: newStatus,
    });

    window.gtag(
      "event",
      "animation_toggle",
      getGtagData("engagement", `Change animation state: ${animationType} (${newStatus})`, {
        animation_type: animationType,
        animation_status: newStatus ? "on" : "off",
      }),
    );

    if (animationType === "dayMode") {
      setCachedState((prev) => ({
        ...prev,
        animations: {
          ...prev.animations,
          headlights: !newStatus,
        },
      }));
    }
  };

  const toggleMenu = () => setExpanded(!expanded);

  // useEffect(() => {
  //   if (cachedState.view === VIEW_TYPES.INTERIOR) {
  //     if (cachedState.animations.headlights) {
  //       setCachedState((prev) => ({
  //         ...prev,
  //         animations: {
  //           ...prev.animations,
  //           headlights: false,
  //         },
  //       }));
  //     }
  //   }
  // }, [cachedState.view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setTopBarHeight(topBarRef.current?.clientHeight || 0);
    });

    if (isMobile) resizeObserver.observe(topBarRef?.current as HTMLDivElement);
    else resizeObserver.unobserve(topBarRef?.current as HTMLDivElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const animation = isMobile
    ? `${expanded ? fadeInAnimation : fadeOutAnimation} 0.3s ease-in-out forwards`
    : "none";

  return (
    <StyledAppBar
      ref={rightPanelRef}
      topBarHeight={topBarHeight}
      disabled={disablePanel}
      isDrawerOpenOnSm={isDrawerOpenOnSm}
    >
      <Stack
        direction="column"
        alignItems="end"
        justifyContent={isMobile ? "flex-start" : "end"}
        rowGap={{ xs: 0.1, sm: 1.5 }}
        sx={{ pointerEvents: "none" }}
      >
        {isMobile && (
          <IconButton
            onClick={toggleMenu}
            disabled={disablePanel}
            sx={{ padding: { xs: 0.5, sm: 1.5 } }}
          >
            {expanded ? <CloseIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}

        <IconButton
          onClick={() => handleAnimationChange("dayMode")}
          toolTipText={dayMode ? "Night Mode" : "Day Mode"}
          disabled={
            isDaymodeLoading || isDoorsOpenLoading || isHeadlightsLoading || isSunroofOpenLoading
          }
          sx={{
            animation,
            padding: { xs: 0.5, sm: 1.5 },
            ...(isMobile && {
              visibility: expanded ? "visible" : "hidden",
            }),
          }}
        >
          {dayMode ? <DarkModeOutlinedIcon /> : <DarkModeIcon />}
        </IconButton>

        {!unavailableAnimations.includes("doorsOpen") && (
          <IconButton
            onClick={() => handleAnimationChange("doorsOpen")}
            toolTipText={!doorsOpen ? "Doors Open" : "Doors Close"}
            disabled={
              isDoorsOpenLoading || isDaymodeLoading || isHeadlightsLoading || isSunroofOpenLoading
            }
            sx={{
              animation,
              padding: { xs: 0.5, sm: 1.5 },
              ...(isMobile && {
                visibility: expanded ? "visible" : "hidden",
              }),
            }}
          >
            {doorsOpen ? <SensorDoorIcon /> : <SensorDoorOutlinedIcon />}
          </IconButton>
        )}

        {!unavailableAnimations.includes("headlights") && (
          <IconButton
            onClick={() => handleAnimationChange("headlights")}
            toolTipText={!headlights ? "Lamps On" : "Lamps Off"}
            disabled={
              isHeadlightsLoading || isDoorsOpenLoading || isDaymodeLoading || isSunroofOpenLoading
            }
            sx={{
              animation,
              padding: { xs: 0.5, sm: 1.5 },
              ...(isMobile && {
                visibility: expanded ? "visible" : "hidden",
              }),
            }}
          >
            {headlights ? <TungstenIcon /> : <TungstenOutlinedIcon />}
          </IconButton>
        )}

        {!unavailableAnimations.includes("sunroofOpen") &&
          cachedState.view === VIEW_TYPES.INTERIOR && (
            <IconButton
              onClick={() => handleAnimationChange("sunroofOpen")}
              toolTipText={!sunroofOpen ? "Open Marsroof" : "Close Marsroof"}
              disabled={
                isSunroofOpenLoading ||
                isHeadlightsLoading ||
                isDoorsOpenLoading ||
                isDaymodeLoading
              }
              sx={{
                animation,
                padding: { xs: 0.5, sm: 1.5 },
                ...(isMobile && {
                  visibility: expanded ? "visible" : "hidden",
                }),
              }}
            >
              {sunroofOpen ? <MinorCrashIcon /> : <MinorCrashOutlinedIcon />}
            </IconButton>
          )}

        {/* {isMobile && (
          <IconButton onClick={handleSaveClick} sx={{ animation }}  disabled={disablePanel}>
            <SaveTwoToneIcon />
          </IconButton>
        )}

        {isMobile && (
          <IconButton onClick={handleShareClick} sx={{ animation }}  disabled={disablePanel}>
            <ShareTwoToneIcon />
          </IconButton>
        )} */}
      </Stack>
    </StyledAppBar>
  );
}

type StyledAppBarProps = {
  topBarHeight: number;
  disabled?: boolean;
  isDrawerOpenOnSm?: boolean;
};

const StyledAppBar = styled("div")<StyledAppBarProps>`
  position: absolute;
  top: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.drawer - 1};
  padding: 2rem 1.75rem 2rem 0;
  display: flex;
  justify-content: space-between;
  height: 100%;
  width: fit-content;
  pointer-events: none;
  transition: filter 0.2s ease;
  ${({ theme }) => theme.breakpoints.between(600, 1100)} {
    padding: 1rem 2.5rem 4rem 0;
    padding: ${(props) =>
      props.isDrawerOpenOnSm ? "1rem 0rem 3rem 1rem" : "1rem 1.5rem 3.5rem 0"};
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: 2rem 0.5rem;
    padding-top: calc(${({ topBarHeight }) => topBarHeight}px);
    overflow-y: scroll;
  }

  & > * {
    filter: blur(${({ disabled }) => (disabled ? "1.5px" : 0)});
    cursor: not-allowed;
  }
`;

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
    z-index: initial;
  }

  100% {
    opacity: 1;
  }
`;

const fadeOutAnimation = keyframes`
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    z-index: -1;
  }
`;

const slideInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateX(100%);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideOutAnimation = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }

  100% {
    opacity: 0;
    transform: translateX(100%);
  }
`;
